import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import type { DataUploadFileResponse } from './types';

type Options = { clientId?: string; familyId?: string };

/**
 * Runs detection on a recording again, replacing the suggestions it still holds under a speaker's
 * old name once the adviser has reassigned that speaker. Invalidates the file the way
 * useFileUpload reads it, so its query picks up the new run.
 */
export const useRegenerateDetections = (
  fileId: string | undefined,
  { clientId, familyId }: Options,
) => {
  const queryClient = useQueryClient();
  const resource = familyId ? `families/${familyId}` : `clients/${clientId}`;

  const { mutateAsync, isPending } = useMutation<{ data: DataUploadFileResponse }, Error, void>({
    mutationFn: () =>
      api.post<DataUploadFileResponse>(
        `${resource}/data-upload-files/${fileId}/regenerate-detections`,
      ),
    // Also on failure: a refusal means a detection is already running, which the refetch shows.
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: familyId
          ? ['families', familyId, 'data-upload-files', fileId]
          : ['clients', clientId, 'data-upload-files', fileId],
      }),
  });

  const regenerateDetections = async (): Promise<void> => {
    await mutateAsync();
  };

  return { regenerateDetections, isRegenerating: isPending };
};
